// Cloudflare Turnstile widget — invisible-to-low-friction spam protection.
//
// Frontend: render <TurnstileWidget action="…" onToken={setToken} /> inside any
// form. Verification is gated by HOSTNAME, not by env presence: it is skipped on
// localhost (so local dev needs no keys) and enforced everywhere else. This
// mirrors the backend's isLocalRequest() check in functions/api/_shared.js.
//
// Backend pairing: functions/api/_shared.js → verifyTurnstile()

import React from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

// Public Turnstile site key. Site keys are PUBLIC by design (they are served to
// the browser in the rendered widget), so committing it is safe and guarantees
// the widget renders in production even when the build env did not provide
// VITE_TURNSTILE_SITE_KEY. Override via that env var for staging/testing with
// Cloudflare's documented always-pass visible test key.
//
// HOSTNAME-SCOPED: this widget must list scopeworth.app (the canonical host)
// AND getscopeworth.com / www.getscopeworth.com — the marketing alias still
// serves the site, so forms load there too — in its allowed hostnames
// (Cloudflare dashboard → Turnstile).
// A host missing from that list fails the challenge, isTurnstileEnabled() still
// returns true off-localhost, and validateSampleReport() then blocks every
// submit on "Please complete the verification" — a silent 100% form outage.
const DEFAULT_SITE_KEY = '0x4AAAAAADPQLYcEAlpoeYQC';

// True when the page is served from a local dev host (localhost / loopback /
// *.local). Verification is skipped here and enforced everywhere else.
function isLocalHost() {
  try {
    if (typeof window === 'undefined' || !window.location) return false;
    const h = window.location.hostname;
    return (
      h === 'localhost' ||
      h === '127.0.0.1' ||
      h === '0.0.0.0' ||
      h === '::1' ||
      h === '[::1]' ||
      h.endsWith('.local')
    );
  } catch (_) {
    return false;
  }
}

// The site key to render with: env override first, else the baked-in public key.
function getSiteKey() {
  try {
    const fromEnv = import.meta && import.meta.env && import.meta.env.VITE_TURNSTILE_SITE_KEY;
    if (fromEnv) return fromEnv;
  } catch (_) {}
  return DEFAULT_SITE_KEY;
}

export function isTurnstileEnabled() {
  // Skip verification on localhost; enforce it on every real host.
  if (isLocalHost()) return false;
  return Boolean(getSiteKey());
}

let scriptLoadPromise = null;
function loadTurnstileScript() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src^="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.turnstile || null), { once: true });
      existing.addEventListener('error', () => reject(new Error('Turnstile failed to load.')), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.turnstile || null);
    s.onerror = () => reject(new Error('Turnstile failed to load.'));
    document.head.appendChild(s);
  });
  return scriptLoadPromise;
}

export function TurnstileWidget({ action, onToken, theme = 'light', size = 'normal' }) {
  const containerRef = React.useRef(null);
  const widgetIdRef = React.useRef(null);
  const onTokenRef = React.useRef(onToken);
  React.useEffect(() => { onTokenRef.current = onToken; }, [onToken]);

  const siteKey = isTurnstileEnabled() ? getSiteKey() : null;

  React.useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    loadTurnstileScript()
      .then((api) => {
        if (cancelled || !api || !containerRef.current) return;
        widgetIdRef.current = api.render(containerRef.current, {
          sitekey: siteKey,
          action: action || 'submit',
          theme,
          size,
          callback: (token) => { try { onTokenRef.current && onTokenRef.current(token); } catch (_) {} },
          'error-callback':   () => { try { onTokenRef.current && onTokenRef.current(null); } catch (_) {} },
          'expired-callback': () => { try { onTokenRef.current && onTokenRef.current(null); } catch (_) {} },
          'timeout-callback': () => { try { onTokenRef.current && onTokenRef.current(null); } catch (_) {} },
        });
      })
      .catch(() => {
        // Script blocked or unreachable — surface as a missing token so the form
        // shows its existing "Please complete the verification" error.
        try { onTokenRef.current && onTokenRef.current(null); } catch (_) {}
      });
    return () => {
      cancelled = true;
      const id = widgetIdRef.current;
      if (id != null && typeof window !== 'undefined' && window.turnstile) {
        try { window.turnstile.remove(id); } catch (_) {}
      }
      widgetIdRef.current = null;
    };
  }, [siteKey, action, theme, size]);

  if (!siteKey) return null;
  return <div ref={containerRef} className="sw-turnstile" aria-label="Verification challenge" />;
}
